import { useEffect } from 'react';
import { App as AntApp, Button, Card, Form, Input, Skeleton, Switch, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { ErrorAlert } from '@/components/ErrorAlert';
import {
  usePlatformAppsQuery,
  usePlatformSettingsQuery,
  useUpdatePlatformAppMutation,
  useUpdatePlatformSettingsMutation,
  type PlatformAppSettings,
  type PlatformSettingsUpdate,
} from './hooks';

const { Title, Text, Paragraph } = Typography;

interface FormValues {
  contact_phone: string;
  contact_email: string;
  contact_telegram: string;
  contact_whatsapp: string;
}

interface AppFormValues {
  brand_name: string;
  email_from: string;
  email_base_url: string;
  listings_enabled: boolean;
}

// Карточка профиля одного приложения (доводка V1 №15/№19): бренд и почта
// (пустое поле = env-дефолт бэкенда), для Light — тумблер раздела
// «Объявления» в кабинете оптики. PATCH /platform/apps/{app}.
function AppSettingsCard({ app }: { app: PlatformAppSettings }): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<AppFormValues>();
  const mutation = useUpdatePlatformAppMutation();

  useEffect(() => {
    form.setFieldsValue({
      brand_name: app.brand_name,
      email_from: app.email_from ?? '',
      email_base_url: app.email_base_url ?? '',
      listings_enabled: app.listings_enabled,
    });
  }, [app, form]);

  const onFinish = (values: AppFormValues): void => {
    mutation.mutate(
      {
        app: app.app,
        brand_name: values.brand_name.trim(),
        email_from: values.email_from.trim(),
        email_base_url: values.email_base_url.trim(),
        ...(app.app === 'light' ? { listings_enabled: values.listings_enabled } : {}),
      },
      { onSuccess: () => void message.success(t('platform.settings.apps.saved')) },
    );
  };

  return (
    <Card
      title={t(`platform.settings.apps.name.${app.app}`)}
      variant="outlined"
      style={{ marginTop: 16 }}
      data-testid={`platform-app-card-${app.app}`}
    >
      <Form<AppFormValues> form={form} layout="vertical" onFinish={onFinish}>
        <Form.Item
          label={t('platform.settings.apps.brand_name')}
          name="brand_name"
          rules={[{ required: true, message: t('platform.settings.apps.brand_name_required') }]}
        >
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item
          label={t('platform.settings.apps.email_from')}
          name="email_from"
          extra={t('platform.settings.apps.env_default_hint')}
        >
          <Input placeholder="Starling <noreply@send.example.com>" maxLength={200} />
        </Form.Item>
        <Form.Item
          label={t('platform.settings.apps.email_base_url')}
          name="email_base_url"
          extra={t('platform.settings.apps.env_default_hint')}
          rules={[
            {
              pattern: /^(https?:\/\/\S+)?$/,
              message: t('platform.settings.apps.email_base_url_invalid'),
            },
          ]}
        >
          <Input placeholder="https://example.kz" maxLength={300} />
        </Form.Item>
        {app.app === 'light' && (
          <Form.Item
            label={t('platform.settings.apps.listings_enabled')}
            name="listings_enabled"
            valuePropName="checked"
            extra={t('platform.settings.apps.listings_enabled_hint')}
          >
            <Switch data-testid="platform-app-listings-switch" />
          </Form.Item>
        )}
        {mutation.error && (
          <div style={{ marginBottom: 16 }}>
            <ErrorAlert error={mutation.error} />
          </div>
        )}
        <Button
          type="primary"
          htmlType="submit"
          loading={mutation.isPending}
          data-testid={`platform-app-save-${app.app}`}
        >
          {t('common.save')}
        </Button>
      </Form>
    </Card>
  );
}

// Платформенные «Настройки» (M5.7) — контакты платформы, которые увидит
// заблокированная оптика (баннер блокировки). Все поля опциональны. PATCH
// /platform/settings. Пороги/квоты SMS переехали в SMS-центр.
// Доводка V1 №15/№19: ниже — профили приложений (Light / Vision).
export function PlatformSettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<FormValues>();
  const { data, isLoading, error } = usePlatformSettingsQuery();
  const mutation = useUpdatePlatformSettingsMutation();
  const apps = usePlatformAppsQuery();

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        contact_phone: data.contact_phone ?? '',
        contact_email: data.contact_email ?? '',
        contact_telegram: data.contact_telegram ?? '',
        contact_whatsapp: data.contact_whatsapp ?? '',
      });
    }
  }, [data, form]);

  const onFinish = (values: FormValues): void => {
    const patch: PlatformSettingsUpdate = {
      contact_phone: values.contact_phone.trim(),
      contact_email: values.contact_email.trim(),
      contact_telegram: values.contact_telegram.trim(),
      contact_whatsapp: values.contact_whatsapp.trim(),
    };
    mutation.mutate(patch, {
      onSuccess: () => {
        void message.success(t('platform.settings.saved'));
      },
    });
  };

  if (isLoading) {
    return (
      <div>
        <Title level={3}>{t('platform.settings.title')}</Title>
        <Skeleton active style={{ marginTop: 16 }} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 680 }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        {t('platform.settings.title')}
      </Title>
      <Text type="secondary">{t('platform.settings.subtitle')}</Text>

      {error && (
        <div style={{ marginTop: 16 }}>
          <ErrorAlert error={error} />
        </div>
      )}

      <Card
        title={t('platform.settings.contacts_title')}
        variant="outlined"
        style={{ marginTop: 16 }}
      >
        <Paragraph type="secondary">{t('platform.settings.contacts_description')}</Paragraph>
        <Form<FormValues> form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item label={t('platform.settings.contact_phone')} name="contact_phone">
            <Input placeholder="+7 700 000 00 00" maxLength={40} />
          </Form.Item>
          <Form.Item
            label={t('platform.settings.contact_email')}
            name="contact_email"
            rules={[{ type: 'email', message: t('platform.settings.email_invalid') }]}
          >
            <Input placeholder="support@starling.kz" maxLength={120} />
          </Form.Item>
          <Form.Item label={t('platform.settings.contact_telegram')} name="contact_telegram">
            <Input placeholder="@starling_support" maxLength={80} />
          </Form.Item>
          <Form.Item label={t('platform.settings.contact_whatsapp')} name="contact_whatsapp">
            <Input placeholder="+7 700 000 00 00" maxLength={40} />
          </Form.Item>

          {mutation.error && (
            <div style={{ marginBottom: 16 }}>
              <ErrorAlert error={mutation.error} />
            </div>
          )}

          <Button
            type="primary"
            htmlType="submit"
            loading={mutation.isPending}
            data-testid="platform-contacts-save"
          >
            {t('common.save')}
          </Button>
        </Form>
      </Card>

      <Title level={4} style={{ marginTop: 32, marginBottom: 4 }}>
        {t('platform.settings.apps.title')}
      </Title>
      <Text type="secondary">{t('platform.settings.apps.description')}</Text>
      {apps.error && (
        <div style={{ marginTop: 16 }}>
          <ErrorAlert error={apps.error} />
        </div>
      )}
      {apps.isLoading && <Skeleton active style={{ marginTop: 16 }} />}
      {apps.data?.map((app) => (
        <AppSettingsCard key={app.app} app={app} />
      ))}
    </div>
  );
}
